/* eslint-disable no-param-reassign */
import fetch from 'cross-fetch';
import { useState, useCallback, useRef, useEffect } from 'react';

import { useUserData } from './useUserData';

export const useHttpClient = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const { userData } = useUserData();

  const activeHttpRequests = useRef([]);

  function normalizeHttpError(err: unknown): string {
    if (err instanceof Error) {
      return err.message;
    }
    if (typeof err === 'object' && err !== null) {
      return (err as any).error?.message || (err as any).message || JSON.stringify(err);
    }
    return String(err);
  }

  const sendRequest = useCallback(
    async (url, method = 'GET', body = null, headers = null) => {
      setError(undefined);
      setIsLoading(true);
      const httpAbortCtrl = new AbortController();
      activeHttpRequests.current.push(httpAbortCtrl);

      if (url.includes(process.env.REACT_APP_BACKEND_URL)) {
        const token = userData?.token;
        if (token && method !== 'GET' && !url.includes('/assessments/datarequest')) {
          headers = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            ...headers,
          };
        } else if (
          (token && method === 'GET') ||
          (token && url.includes('/assessments/datarequest'))
        ) {
          headers = {
            Authorization: `Bearer ${token}`,
            ...headers,
          };
        } else {
          headers = {
            'Content-Type': 'application/json',
            ...headers,
          };
        }
      }

      try {
        const response = await fetch(url, {
          method,
          body,
          headers,
          signal: httpAbortCtrl.signal,
        });

        const responseData = await response.text().then((text) => {
          try {
            return text ? JSON.parse(text) : {};
          } catch {
            return text;
          }
        });

        activeHttpRequests.current = activeHttpRequests.current.filter(
          (reqCtrl) => reqCtrl !== httpAbortCtrl
        );

        if (!response.ok) {
          const message =
            responseData?.error?.message ||
            responseData?.message ||
            (typeof responseData === 'string' ? responseData : 'Request failed');
          throw new Error(message);
        }

        setIsLoading(false);
        return responseData;
      } catch (err) {
        const errorMessage = normalizeHttpError(err);
        setError(errorMessage);
        setIsLoading(false);
        throw errorMessage;
      }
    },
    [userData?.token]
  );

  const clearError = () => {
    setError(null);
  };

  return { isLoading, error, sendRequest, clearError };
};
